// FundAllocationDashboard.js
import React, { useEffect, useState } from "react";
import { ResponsiveBar } from "@nivo/bar";
import axios from "axios";
import { API_BASE } from "../config";
import { useNavigate } from "react-router-dom";

export default function FundAllocationDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState([]);

  // ------------------ CURRENT FY ------------------
  const getCurrentFY = () => {
    const t = new Date();
    const y = t.getFullYear();
    const m = t.getMonth() + 1;
    return m < 4 ? `${y - 1}-${y}` : `${y}-${y + 1}`;
  };

  const currentFY = getCurrentFY();
  const [selectedFY, setSelectedFY] = useState(currentFY);

  // FY → Start & End Dates
  const getFYDates = (fy) => {
    const [start, end] = fy.split("-");
    return { fy_start: `${start}-04-01`, fy_end: `${end}-03-31` };
  };

  // ------------------ FETCH SUMMARY DATA ------------------
  const fetchSummary = async (fy = currentFY) => {
    try {
      const { fy_start, fy_end } = getFYDates(fy);

      const res = await axios.get(`${API_BASE}/fund-allocation-analysis`, {
        params: { fy_start, fy_end }
      });

      const mapped = res.data.map((r) => ({
        district: r.district_name,
        dist_id: r.dist_id,
        "All School": Number(r.all_school_amount || 0),
        "Selected School": Number(r.selected_school_amount || 0),
      }));

      setData(mapped);

    } catch (err) {
      console.error("fetchSummary error:", err);
      setData([]);
    }
  };

  const barChartSettings = {
    keys: ["count"],
    indexBy: "name",
    margin: { top: 80, right: 50, bottom: 160, left: 70 }, // increased
    padding: 0.35,
    colors: (bar) => ["#FF6384", "#36A2EB", "#FFCE56", "#4BC0C0"][bar.index % 4],
    borderColor: { from: "color", modifiers: [["darker", 1.2]] },
  
    axisBottom: {
      tickRotation: -35,
      tickSize: 5,
      tickPadding: 15, // increased
      legend: "District Names",
      legendPosition: "middle",
      legendOffset: 100, // moved further down
    },
  
    axisLeft: {
      legend: "Fund Allocation Amount",
      legendPosition: "middle",
      legendOffset: -90,
    },
  
    borderRadius: 6,
    labelSkipWidth: 12,
    labelSkipHeight: 12,
    labelTextColor: "#000",
  
    legends: [
      {
        dataFrom: "keys",
        anchor: "bottom",
        direction: "row",
        translateY: 80,
        itemsSpacing: 12,
        itemWidth: 140,
        itemHeight: 20,
        symbolSize: 18,
      },
    ],
  };

  useEffect(() => {
    fetchSummary(currentFY);
  }, []);

  // ------------------ CLICK HANDLER (only for Selected School) ------------------
  const handleBarClick = (bar) => {
    if (String(bar.id) !== "Selected School") return;
  
    const districtId = bar.data?.dist_id;
    if (!districtId) return;
  
    navigate("/fund-utilisation-details", {
      state: {
        district: districtId,
        fy: selectedFY
      }
    });
  };
  

  // ------------------ UI ------------------
  return (
    <div style={{ padding: 24 }}>
      <h2>Fund Allocation Status — All School vs Selected School</h2>

      {/* Filters */}
      <div style={{ display: "flex", gap: 12, marginBottom: 16 }}>
        <label>
          Financial Year:
          <select
            value={selectedFY}
            onChange={(e) => setSelectedFY(e.target.value)}
            style={{ marginLeft: 8, height: "36px" }}
          >
            {[currentFY,
              `${Number(currentFY.split("-")[0]) - 1}-${Number(currentFY.split("-")[1]) - 1}`,
              `${Number(currentFY.split("-")[0]) + 1}-${Number(currentFY.split("-")[1]) + 1}`
            ].map((fy) => (
              <option key={fy} value={fy}>{fy}</option>
            ))}
          </select>
        </label>

        <button
          onClick={() => fetchSummary(selectedFY)}
          style={{
            padding: "8px 14px",
            cursor: "pointer",
            background: "#838be0",
            borderRadius: "5px",
            color: "#fff",
            border: "none"
          }}
        >
          Search
        </button>
      </div>

      {/* BAR CHART */}
      <div style={{ height: 520, background: "#fff", padding: 12, borderRadius: 8 }}>
        <ResponsiveBar
          data={data}
          {...barChartSettings}
          keys={["All School", "Selected School"]}
          indexBy="district"
          margin={{ top: 80, right: 80, bottom: 160, left: 120 }}
          padding={0.3}
          groupMode="grouped"
          colors={["#4caf50", "#ff9800"]} // green, orange
          axisBottom={{ tickRotation: -35 }}

          // ------------------ Per-bar interactions ------------------
          // Click: only trigger action when bar.id === "Selected School"
          onClick={(bar, event) => {
            handleBarClick(bar);
          }}

          // Hover: show pointer cursor only for Selected School
          onMouseEnter={(bar, event) => {
            if (String(bar.id) === "Selected School") {
              document.body.style.cursor = "pointer";
            } else {
              document.body.style.cursor = "default";
            }
          }}
          onMouseLeave={() => {
            document.body.style.cursor = "default";
          }}

          // keep default labels or disable if you prefer custom labels
          enableLabel={false}
          label={(bar) => `₹${Number(bar.value).toLocaleString()}`}
          labelTextColor="#000"

          // tooltip unchanged
          tooltip={({ id, value, indexValue }) => (
            <div style={{ background: "#fff", padding: 8, borderRadius: 6 }}>
              <strong>{indexValue}</strong>
              <div>{id}: ₹{Number(value || 0).toLocaleString()}</div>
            </div>
          )}
        />
      </div>

      <p style={{ marginTop: 12, color: "#666" }}>
        👉 Click on the <span style={{ color: "#ff9800", fontWeight: 700 }}>Selected School</span> (orange) bar to view Allocation vs Utilisation details. Green (All School) bars are not clickable.
      </p>
    </div>
  );
}
